import { useEffect, useState } from "react";
import type { MatterSummary } from "./api.js";
import { routeForPath } from "./routing.js";

export interface MattersState {
  readonly matters: readonly MatterSummary[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly setMatters: React.Dispatch<React.SetStateAction<readonly MatterSummary[]>>;
  /** The matter named by a deep link, resolved once the list arrives (D142). */
  readonly deepLinked: MatterSummary | null;
}

/**
 * Loads the matter list once the session is known.
 *
 * It also resolves a deep link: `/matters/:id` carries an id but not the
 * matter, so opening that URL directly showed an empty workspace until the id
 * could be matched against the loaded list.
 */
export const useMatters = (signedIn: boolean): MattersState => {
  const [matters, setMatters] = useState<readonly MatterSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deepLinked, setDeepLinked] = useState<MatterSummary | null>(null);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void import("./api.js")
      .then(({ listMatters }) => listMatters())
      .then((result) => {
        if (cancelled) return undefined;
        setMatters(result.matters);
        const wanted = routeForPath(globalThis.location.pathname).matterId;
        if (wanted !== null) {
          setDeepLinked(result.matters.find(({ id }) => id === wanted) ?? null);
        }
        return undefined;
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(
            caught instanceof Error ? caught.message : "Could not load matters.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  return { matters, loading, error, setMatters, deepLinked };
};
